import React, { useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { classNames } from 'primereact/utils';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Userlogin } from '../redux/slices/authenticationSlice';
import usePreferredLanguage from '../utilities/usePreferredLanguage';
import { translateUiText } from '../utilities/siteTranslations';

const Login = (props) => {
    const [loading, setloading] = useState(false);
    const [loadingIcon, setloadingIcon] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const dispatch = useDispatch();

    const navigate = useNavigate();
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);

    const handleUrl = () => {
        navigate('/forgetpassword');
    };

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                email: Yup.string().trim().email(t('Please enter a valid email')).required(t('Email is required')),
                password: Yup.string().trim().required(t('Password is required'))
            }),
        [preferredLanguage]
    );

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema,
        onSubmit: async (data) => {
            setloading(true);
            setloadingIcon('pi pi-spin pi-spinner');
            try {
                await dispatch(Userlogin({ navigate: navigate, formData: data }));
            } finally {
                setloading(false);
                setloadingIcon('');
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    return (
        <div className="login-full-bg flex align-items-center justify-content-center min-h-screen px-3 py-4">
            <div className="login-overlay" />

            <div className="login-layout flex flex-column md:flex-row align-items-start md:align-items-start justify-content-center w-full" style={{ maxWidth: '960px' }}>
                {/* Left logo / brand area */}
                <div className="flex align-items-start justify-content-center md:justify-content-start mb-4 md:mb-0 mr-0 md:mr-5">
                    <div className="flex flex-column align-items-flex-start gap-3" style={{ maxWidth: '420px' }}>
                        <div className="flex align-items-center gap-3">
                            <div className="login-left-logo">
                                <img src="assets/layout/images/SYNCRO.png" alt="SYNCRO logo" style={{ height: '80px', width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                            </div>
                        </div>
                        <div className="text-left">
                            <p className="mt-3 mb-3 text-base" style={{ color: '#000', fontWeight: 500 }}>
                                {t('Admin portal to manage machines, teams, users and complaints in one place.')}
                            </p>
                            <div className="flex flex-column gap-1 text-base font-medium" style={{ color: '#000' }}>
                                <span className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-xs" />
                                    <span>{t('Monitor machine status, locations and routes in real time.')}</span>
                                </span>
                                <span className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-xs" />
                                    <span>{t('Track complaints, chat with customers and close tickets faster.')}</span>
                                </span>
                                <span className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-xs" />
                                    <span>{t('Control users, teams and spare parts inventory from one place.')}</span>
                                </span>
                                </div>
                                <div className="mt-4 text-xs text-500 text-left">
                                    <span>
                                        © {new Date().getFullYear()} {t('SYNCRO. All rights reserved. This admin portal is intended for authorised staff only and provides access to operational data, analytics and customer interactions.')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                {/* Divider (desktop only) */}
                <div className="login-divider hidden md:block" />

                {/* Right form area */}
                <div className="login-right w-full md:w-5">
                    <div className="mb-4 text-center md:text-left">
                        <p className="Signin__Heading mb-1">{t('Welcome')}</p>
                        <span className="text-sm login-subtitle-text">{t('Please login to admin dashboard')}</span>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="flex flex-column gap-3">
                        <div className="mb-1">
                            <label htmlFor="email" className="text-sm font-medium mb-2 block">
                                {t('Email')}
                            </label>
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-envelope" />
                                <InputText
                                    type="email"
                                    id="email"
                                    className={classNames({ 'p-invalid': isFormFieldValid('email') }, 'Login__Input')}
                                    name="email"
                                    value={formik.values.email}
                                    placeholder={t('Enter your email')}
                                    onChange={formik.handleChange}
                                    autoComplete="email"
                                />
                            </span>
                            {getFormErrorMessage('email')}
                        </div>

                        <div className="mb-1">
                            <label htmlFor="password" className="text-sm font-medium mb-2 block">
                                {t('Password')}
                            </label>
                            <span className="p-input-icon-left p-input-icon-right w-full">
                                <i className="pi pi-key" />
                                <InputText
                                    id="password"
                                    className={classNames({ 'p-invalid': isFormFieldValid('password') }, 'Login__Input')}
                                    name="password"
                                    placeholder={t('Enter your password')}
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                />
                                <i
                                    className={classNames('pi cursor-pointer', {
                                        'pi-eye': !showPassword,
                                        'pi-eye-slash': showPassword
                                    })}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                />
                            </span>
                            {getFormErrorMessage('password')}
                        </div>

                        <div className="flex align-items-center justify-content-between mb-2 mt-2">
                            <span className="text-xs" style={{ color: '#000' }}>{t('Use your admin credentials to sign in.')}</span>
                            <button type="button" className="p-link text-xs forgot_label" onClick={handleUrl}>
                                {t('Forgot password?')}
                            </button>
                        </div>

                        <Button
                            type="submit"
                            label={t('Login')}
                            icon={loadingIcon || 'pi pi-sign-in'}
                            iconPos="right"
                            className="Login__Button w-full"
                            loading={loading}
                        />
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
